import { useEffect, useState, useCallback, useRef } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { ROUTE_QUERY_PARAM } from '../../app/Routes/const';
import { useListFetch, useFetch, useDeepLinkFilter } from '../../hooks';
import { debounce } from 'lodash';
import {
  useShouldUpdateImmediately,
  useShouldRefreshForm,
  processAnalyticsFilterForAPI,
  buildConversationAPIParams,
  processMetricChanges,
  getChangedFields,
  hasImmediateChanges,
  handleAPIUpdateStrategy,
  getStoredSettings,
  saveSettings,
} from '../../utilities';
import {
  getConversationCountries,
  getConversations,
  getShortAgents,
  exportConversations,
} from '../../api';
import { countries } from './countries';
import { ALL_CHANNEL } from './const';
import type { ConversationResponse } from '../../api';
import type { CountryFilterMode } from '../../components/CountryFilterDropdown';
import { notify } from '../../app';

export const CONVERSATIONS_STORAGE_KEY = 'conversations_settings';

const masterAgentId =
  process.env.REACT_APP_MASTER_AGENT_ID ||
  '85c6b483-2ab0-4e36-8207-e0a3c6ac1aad';

const DEFAULT_COUNTRY_FILTERS = {
  selected: ['UA'],
  mode: 'EXCLUDE' as CountryFilterMode,
};

export const useShortAgents = (): OptionType[] => {
  const { data } = useFetch(() => getShortAgents(), []);

  return (data ?? []).map(d => ({ value: d.id ?? '', label: d.title }));
};

export const useConversationCountries = (): OptionType[] => {
  const { data } = useFetch(() => getConversationCountries(), []);

  return (data ?? [])
    .filter((code): code is string => Boolean(code))
    .map(code => countries.find(opt => opt.value === code))
    .filter((country): country is OptionType => Boolean(country));
};

const getInitialFilters = (storedSettings: any) => {
  if (storedSettings?.filters) {
    return {
      ...storedSettings.filters,
      countries: storedSettings.filters.countries || DEFAULT_COUNTRY_FILTERS,
      completed_conversations:
        storedSettings.filters.completed_conversations !== undefined
          ? storedSettings.filters.completed_conversations
          : true,
      conversation_channel:
        storedSettings.filters.conversation_channel || ALL_CHANNEL,
    };
  }

  return {
    countries: DEFAULT_COUNTRY_FILTERS,
    completed_conversations: true,
    conversation_channel: ALL_CHANNEL,
  };
};

export const useConversations = () => {
  const { params } = useRouteMatch<{ organization_id?: string }>();
  const organizationId = params?.organization_id;

  const agentsOptions = useShortAgents();
  const storedSettings = getStoredSettings(CONVERSATIONS_STORAGE_KEY);
  const initialFiltersBase = getInitialFilters(storedSettings);

  const { initialFilters, parseFiltersData } = useDeepLinkFilter({
    params: [
      { paramName: ROUTE_QUERY_PARAM.USER_ID },
      { paramName: ROUTE_QUERY_PARAM.USER_PHONE },
    ],
    baseFilters: initialFiltersBase,
  });

  const [formId, setFormId] = useState(1);
  const [uiFilters, setUIFilters] = useState(initialFilters);
  const [exporting, setExporting] = useState(false);

  const {
    nodes,
    filters: apiFilters,
    setFilters: setAPIFilters,
    sort,
    setSort,
    intializing: loading,
    ...rest
  } = useListFetch<ConversationResponse>(
    params => {
      const {
        page,
        perPage,
        params: apiParams,
      } = buildConversationAPIParams(
        params?.filters,
        params?.pagination,
        masterAgentId
      );

      return getConversations(page, perPage, apiParams, params?.sort);
    },
    {
      deps: [masterAgentId],
      withQueryParams: true,
      resultKey: 'conversations',
      initialFilters,
      parseFiltersData,
      initialSort: storedSettings?.sort ?? {
        name: 'messagesCount',
        direction: 'desc',
      },
    }
  );

  const debouncedSetFilters = useRef(
    debounce((newValues: Record<string, any>) => {
      setAPIFilters(newValues);
    }, 500)
  ).current;

  const shouldUpdateImmediately = useShouldUpdateImmediately();
  const shouldRefreshForm = useShouldRefreshForm(uiFilters);

  const handleFiltersChange = useCallback(
    (newValues: Record<string, any>) => {
      const { processedValues, isMetricNameChange } = processMetricChanges(
        newValues,
        uiFilters
      );

      setUIFilters(processedValues);

      const finalValues = processAnalyticsFilterForAPI(
        processedValues,
        isMetricNameChange
      );

      if (finalValues === null) return;

      const changedFields = getChangedFields(processedValues, apiFilters || {});
      const isImmediate = hasImmediateChanges(
        changedFields,
        processedValues,
        apiFilters || {},
        shouldUpdateImmediately
      );

      const wasImmediateUpdate = handleAPIUpdateStrategy(
        finalValues,
        isImmediate,
        isMetricNameChange,
        debouncedSetFilters,
        setAPIFilters
      );

      if (
        wasImmediateUpdate &&
        (shouldRefreshForm(processedValues) || isMetricNameChange)
      ) {
        setFormId(f => f + 1);
      }
    },
    [
      uiFilters,
      apiFilters,
      shouldUpdateImmediately,
      shouldRefreshForm,
      debouncedSetFilters,
      setAPIFilters,
    ]
  );

  useEffect(() => {
    saveSettings(CONVERSATIONS_STORAGE_KEY, apiFilters, sort);
  }, [apiFilters, sort]);

  useEffect(() => {
    return () => {
      debouncedSetFilters.cancel();
    };
  }, [debouncedSetFilters]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const { params: apiParams } = buildConversationAPIParams(
        apiFilters || uiFilters,
        { page: 0, perPage: 1 },
        masterAgentId
      );

      const { blob, filename } = await exportConversations(apiParams);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      notify.success('Conversations exported successfully');
    } catch (error) {
      notify.error('Failed to export conversations');
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  }, [apiFilters, uiFilters]);

  return {
    ...rest,
    sort,
    formId,
    setSort,
    filters: uiFilters,
    setFilters: handleFiltersChange,
    loading,
    conversations: nodes ?? [],
    agentsOptions,
    organizationId,
    exportConversations: handleExport,
    exporting,
  };
};
